import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  UnorderedList,
  ListItem,
  Link,
  useColorModeValue,
  Kbd,
  Flex,
  Spacer
} from '@chakra-ui/react';
import {
  HelpCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  ExternalLink,
  BookOpen,
  MessageCircle,
  Zap,
  Settings,
  BarChart3
} from 'lucide-react';

interface HelpCenterProps {
  currentPage?: string;
  onOpenKnowledgeBase: () => void;
  onOpenTutorial: () => void;
}

const HelpCenter: React.FC<HelpCenterProps> = ({ 
  currentPage = 'dashboard', 
  onOpenKnowledgeBase, 
  onOpenTutorial 
}) => {
  const [systemStatus, setSystemStatus] = useState<'healthy' | 'warning' | 'error'>('healthy');
  const [apiStatus, setApiStatus] = useState<'connected' | 'disconnected' | 'loading'>('loading');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Check system status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Use environment-aware API URL
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/health`);
        if (response.ok) {
          setApiStatus('connected');
          setSystemStatus('healthy');
        } else {
          setApiStatus('disconnected');
          setSystemStatus('error');
        }
      } catch (error) {
        setApiStatus('disconnected');
        setSystemStatus('error');
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Context-specific help content
  const getContextualHelp = () => {
    switch (currentPage) {
      case 'rwa-playbook':
        return {
          title: 'RWA Playbook Help',
          tips: [
            'Look for HIGH confidence signals (>85%) for best results',
            'Green BUY signals suggest good entry points',
            'Use backtesting before making any trades',
            'Start with VBILL for low-risk exposure'
          ],
          quickActions: [
            { text: 'How to read signals', action: 'signals-guide' },
            { text: 'Backtesting tutorial', action: 'backtest-guide' },
            { text: 'RWA asset guide', action: 'rwa-assets' }
          ]
        };
      case 'discovery':
        return {
          title: 'Discovery Help',
          tips: [
            'Use filters to narrow down opportunities',
            'Check volume before considering any asset',
            'Sort by confidence for best signals first',
            'Market cap indicates asset stability'
          ],
          quickActions: [
            { text: 'How to use filters', action: 'filters-guide' },
            { text: 'Understanding metrics', action: 'metrics-guide' }
          ]
        };
      default:
        return {
          title: 'General Help',
          tips: [
            'Navigate using the tabs at the top',
            'Check system status in the top-right',
            'Use Ctrl+? to open help anytime',
            'Start with the tutorial if you\'re new'
          ],
          quickActions: [
            { text: 'Take the tutorial', action: 'tutorial' },
            { text: 'Browse knowledge base', action: 'knowledge-base' }
          ]
        };
    }
  };

  const contextHelp = getContextualHelp();

  const troubleshootingSteps = [
    {
      issue: 'RWA signals not loading',
      steps: [
        'Check if API is running (should show "Connected" below)',
        'Refresh the page with Ctrl+F5',
        'Clear browser cache',
        'Check if port 3001 is blocked by firewall'
      ]
    },
    {
      issue: 'Backtest not working',
      steps: [
        'Ensure Python dependencies are installed',
        'Check that date range is valid',
        'Use only supported assets (ONDO, CFG, VBILL, ETH, BLK)',
        'Try reducing Monte Carlo simulation count'
      ]
    },
    {
      issue: 'Charts not displaying',
      steps: [
        'Check internet connection',
        'Disable ad blockers temporarily',
        'Try a different browser',
        'Clear browser data and restart'
      ]
    }
  ];

  const StatusIndicator = ({ status, label }: { status: string; label: string }) => {
    const getStatusProps = () => {
      switch (status) {
        case 'connected':
        case 'healthy':
          return { color: 'green', icon: CheckCircle, text: 'Connected' };
        case 'disconnected':
        case 'error':
          return { color: 'red', icon: AlertTriangle, text: 'Disconnected' };
        case 'warning':
          return { color: 'yellow', icon: AlertTriangle, text: 'Warning' };
        default:
          return { color: 'gray', icon: Info, text: 'Loading...' };
      }
    };

    const { color, icon, text } = getStatusProps();

    return (
      <HStack spacing={2}>
        <Icon as={icon} color={`${color}.500`} boxSize={4} />
        <Text fontSize="sm" color={`${color}.600`}>
          {label}: {text}
        </Text>
      </HStack>
    );
  };

  return (
    <Box 
      position="fixed" 
      bottom={4} 
      right={4} 
      zIndex={1000}
    >
      <VStack spacing={2} align="end">
        {/* System Status */}
        <Box 
          bg={bgColor} 
          border="1px solid" 
          borderColor={borderColor}
          borderRadius="lg" 
          p={3} 
          shadow="lg"
          minW="200px"
        >
          <VStack spacing={2} align="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              System Status
            </Text>
            <StatusIndicator status={apiStatus} label="API" />
            <StatusIndicator status={systemStatus} label="Platform" />
            
            {systemStatus === 'error' && (
              <Alert status="error" size="sm" mt={2}>
                <AlertIcon />
                <Box fontSize="xs">
                  <AlertTitle>Connection Issue!</AlertTitle>
                  <AlertDescription>
                    Check troubleshooting below
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </VStack>
        </Box>

        {/* Main Help Button */}
        <Popover placement="top-end">
          <PopoverTrigger>
            <Button
              colorScheme="purple"
              size="lg"
              borderRadius="full"
              boxShadow="xl"
              leftIcon={<HelpCircle size={20} />}
              _hover={{ transform: 'scale(1.05)' }}
              transition="all 0.2s"
            >
              Help
            </Button>
          </PopoverTrigger>
          <PopoverContent w="400px">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>
              <HStack>
                <Icon as={HelpCircle} color="purple.500" />
                <Text fontWeight="bold">{contextHelp.title}</Text>
              </HStack>
            </PopoverHeader>
            <PopoverBody>
              <VStack spacing={4} align="stretch">
                
                {/* Context Tips */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                    💡 Quick Tips for This Page:
                  </Text>
                  <UnorderedList spacing={1}>
                    {contextHelp.tips.map((tip, idx) => (
                      <ListItem key={idx} fontSize="sm" color="gray.600">
                        {tip}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Box>

                {/* Quick Actions */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                    🚀 Quick Actions:
                  </Text>
                  <VStack spacing={2}>
                    {contextHelp.quickActions.map((action, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                        leftIcon={<ExternalLink size={14} />}
                        width="full"
                        justifyContent="flex-start"
                        onClick={() => {
                          if (action.action === 'tutorial') onOpenTutorial();
                          else if (action.action === 'knowledge-base') onOpenKnowledgeBase();
                          // Add more action handlers as needed
                        }}
                      >
                        {action.text}
                      </Button>
                    ))}
                  </VStack>
                </Box>

                {/* Main Help Resources */}
                <Box pt={2} borderTop="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                    📚 Help Resources:
                  </Text>
                  <VStack spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      leftIcon={<BookOpen size={14} />}
                      width="full"
                      justifyContent="flex-start"
                      onClick={onOpenKnowledgeBase}
                    >
                      Knowledge Base
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="green"
                      leftIcon={<Zap size={14} />}
                      width="full"
                      justifyContent="flex-start"
                      onClick={onOpenTutorial}
                    >
                      Beginner Tutorial
                    </Button>
                  </VStack>
                </Box>

                {/* Troubleshooting */}
                {systemStatus === 'error' && (
                  <Box pt={2} borderTop="1px solid" borderColor="red.200">
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color="red.700">
                      🔧 Troubleshooting:
                    </Text>
                    <VStack spacing={3} align="stretch">
                      {troubleshootingSteps.map((item, idx) => (
                        <Box key={idx}>
                          <Text fontSize="xs" fontWeight="semibold" color="red.800" mb={1}>
                            {item.issue}:
                          </Text>
                          <UnorderedList spacing={0}>
                            {item.steps.map((step, stepIdx) => (
                              <ListItem key={stepIdx} fontSize="xs" color="red.600" ml={2}>
                                {step}
                              </ListItem>
                            ))}
                          </UnorderedList>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Keyboard Shortcuts */}
                <Box pt={2} borderTop="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                    ⌨️ Keyboard Shortcuts:
                  </Text>
                  <VStack spacing={1} align="stretch">
                    <Flex>
                      <Text fontSize="xs" color="gray.600">Open Help:</Text>
                      <Spacer />
                      <Kbd fontSize="xs">Ctrl + ?</Kbd>
                    </Flex>
                    <Flex>
                      <Text fontSize="xs" color="gray.600">Refresh Page:</Text>
                      <Spacer />
                      <Kbd fontSize="xs">Ctrl + F5</Kbd>
                    </Flex>
                    <Flex>
                      <Text fontSize="xs" color="gray.600">Open Tutorial:</Text>
                      <Spacer />
                      <Kbd fontSize="xs">Ctrl + T</Kbd>
                    </Flex>
                  </VStack>
                </Box>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </VStack>
    </Box>
  );
};

export default HelpCenter;